import axios from 'axios'
import {
  CASE_IMAGE_VARIANT_ERROR_CODE,
  type CaseImageVariantErrorCode,
} from '@messenger/consts'

/** True when `value` is one of the codes this build knows how to explain. */
export const isCaseImageVariantErrorCode = (
  value: unknown,
): value is CaseImageVariantErrorCode =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(CASE_IMAGE_VARIANT_ERROR_CODE, value)

/**
 * Codes where trying again right now cannot help: the cap or spend limit is
 * reached, the image was blocked, the photo is too wide or tall for the
 * model, the feature is not set up, or AI image edits are switched off. The UI hides
 * "Try again" for these so nobody keeps re-queueing a generation that will
 * fail the same way.
 */
const FINAL_ERROR_CODES: ReadonlySet<CaseImageVariantErrorCode> = new Set([
  CASE_IMAGE_VARIANT_ERROR_CODE.DAILY_CAP_REACHED,
  CASE_IMAGE_VARIANT_ERROR_CODE.MODERATION_BLOCKED,
  CASE_IMAGE_VARIANT_ERROR_CODE.SPEND_LIMIT,
  CASE_IMAGE_VARIANT_ERROR_CODE.NOT_CONFIGURED,
  CASE_IMAGE_VARIANT_ERROR_CODE.ASPECT_UNSUPPORTED,
  CASE_IMAGE_VARIANT_ERROR_CODE.AI_IMAGE_EDITS_DISABLED,
])

/**
 * Whether a process or accept request was refused because AI image edits
 * are switched off: the backend's 403 whose body carries
 * `AI_IMAGE_EDITS_DISABLED`. A 403 with any other body (a case the caller
 * may not act on) is not this refusal.
 */
export const isAiImageEditsDisabledError = (err: unknown): boolean => {
  if (!axios.isAxiosError(err) || err.response?.status !== 403) return false
  const body = err.response.data as { code?: unknown } | undefined
  return body?.code === CASE_IMAGE_VARIANT_ERROR_CODE.AI_IMAGE_EDITS_DISABLED
}

/**
 * Whether a failed write may still have committed: the request got no
 * response (a timeout or a dropped connection), or a 502, 503 or 504 came
 * back, whatever its body, since a gateway answers those in the backend's
 * place after a forwarded write may have landed. Any other answer came from
 * the backend and means nothing changed. Enhance and Remove Background read
 * the server's state again after such a failure instead of assuming either
 * outcome.
 */
export const mayHaveCommitted = (err: unknown): boolean =>
  axios.isAxiosError(err) &&
  (!err.response || [502, 503, 504].includes(err.response.status))

/**
 * Whether "Try again" should be offered for a failure. Transient codes
 * (model error, enqueue failure) and unknown or missing codes are retryable:
 * an unfamiliar reason must not strand the user without a way forward.
 */
export const isRetryableVariantErrorCode = (
  code: string | null | undefined,
): boolean => !isCaseImageVariantErrorCode(code) || !FINAL_ERROR_CODES.has(code)

/**
 * The locale key that explains a failure. Unknown codes fall back to the
 * generic "We couldn't enhance this image this time." copy.
 */
export const variantErrorLocaleKey = (
  code: string | null | undefined,
): string =>
  isCaseImageVariantErrorCode(code)
    ? `caseImages.enhance.errors.${code}`
    : 'caseImages.enhance.errorTitle'
